import { useTheme } from '../context/ThemeContext';
import { useState, useEffect, useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { getCategories, addCategory, deleteCategory, updateCategory,
         getIncome, addIncome, updateIncome, deleteIncome,
         getHousehold, updateHousehold, inviteMember,
         getAccounts, syncPlaid, updateAccount,
         createLinkToken, exchangeToken,
         getPlaidMappings, updatePlaidMapping,
         getPlaidBanks, disconnectBank, createUpdateLinkToken,
         syncGoogleSheets, getGoogleSyncStatus,
         getPriorities, savePriorities,
         getSavingsGoals, createSavingsGoal, updateSavingsGoal, deleteSavingsGoal,
         getDebts, createDebt, updateDebt, deleteDebt,
         getFinancialFocus, updateFinancialFocus } from '../api/client';
import { useAuth } from '../context/AuthContext';


const fmt = (n) => `$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Badge = ({ label, color }) => (
  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", padding: "3px 7px", borderRadius: 4, background: color + "22", color, border: `1px solid ${color}33` }}>
    {label}
  </span>
);

const scheduleLabel = (src) => {
  if (src.schedule_type === 'weekly')       return `Every ${src.week_day}`;
  if (src.schedule_type === 'biweekly')     return `Every other ${src.week_day}`;
  if (src.schedule_type === 'monthly')      return `Monthly on the ${src.month_day}th`;
  if (src.schedule_type === 'twiceMonthly') return `${src.month_day_1}th & ${src.month_day_2}th`;
  return src.schedule_type || '';
};

const getCurrentPeriod = (monthStart = 26) => {
  const today = new Date();
  let start, end;
  if (today.getDate() >= monthStart) {
    start = new Date(today.getFullYear(), today.getMonth(), monthStart);
    end   = new Date(today.getFullYear(), today.getMonth() + 1, monthStart - 1);
  } else {
    start = new Date(today.getFullYear(), today.getMonth() - 1, monthStart);
    end   = new Date(today.getFullYear(), today.getMonth(), monthStart - 1);
  }
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};


// ─── Savings & Debt Tab ───────────────────────────────────────────────────────
const SavingsDebtTab = ({ COLORS, accounts }) => {
  const [goals,        setGoals]        = useState([]);
  const [debts,        setDebts]        = useState([]);
  const [focus,        setFocus]        = useState(50);
  const [strategy,     setStrategy]     = useState("avalanche");
  const [loading,      setLoading]      = useState(true);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [showDebtForm, setShowDebtForm] = useState(false);
  const [newGoal,      setNewGoal]      = useState({ name: "", target_amount: "", target_date: "", account_id: "" });
  const [newDebt,      setNewDebt]      = useState({ name: "", balance: "", interest_rate: "", minimum_payment: "", account_id: "", debt_type: "credit_card" });
  const [focusSaving,  setFocusSaving]  = useState(false);

  const savingsAccounts = accounts.filter(a => a.type === "depository");
  const creditAccounts  = accounts.filter(a => a.type === "credit");

  useEffect(() => {
    const load = async () => {
      try {
        const [gRes, dRes, fRes] = await Promise.all([
          getSavingsGoals(), getDebts(), getFinancialFocus()
        ]);
        setGoals(Array.isArray(gRes.data) ? gRes.data : []);
        setDebts(Array.isArray(dRes.data) ? dRes.data : []);
        setFocus(fRes.data?.financial_focus ?? 50);
        setStrategy(fRes.data?.debt_strategy || "avalanche");
      } catch(e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleFocusChange = async (val) => {
    setFocus(val);
  };

  const handleFocusSave = async () => {
    setFocusSaving(true);
    try {
      await updateFinancialFocus({ financial_focus: focus, debt_strategy: strategy });
    } catch(e) { console.error(e); }
    finally { setFocusSaving(false); }
  };

  const handleAddGoal = async () => {
    if (!newGoal.name) return;
    try {
      const res = await createSavingsGoal({
        name: newGoal.name,
        target_amount: newGoal.target_amount ? parseFloat(newGoal.target_amount) : null,
        target_date: newGoal.target_date || null,
        account_id: newGoal.account_id ? parseInt(newGoal.account_id) : null,
      });
      setGoals(prev => [...prev, res.data]);
      setNewGoal({ name: "", target_amount: "", target_date: "", account_id: "" });
      setShowGoalForm(false);
    } catch(e) { console.error(e); }
  };

  const handleDeleteGoal = async (id) => {
    try {
      await deleteSavingsGoal(id);
      setGoals(prev => prev.filter(g => g.id !== id));
    } catch(e) { console.error(e); }
  };

  const handleAddDebt = async () => {
    if (!newDebt.name) return;
    try {
      const res = await createDebt({
        name: newDebt.name,
        balance: parseFloat(newDebt.balance) || 0,
        interest_rate: parseFloat(newDebt.interest_rate) || 0,
        minimum_payment: parseFloat(newDebt.minimum_payment) || 0,
        account_id: newDebt.account_id ? parseInt(newDebt.account_id) : null,
        debt_type: newDebt.debt_type,
      });
      setDebts(prev => [...prev, res.data]);
      setNewDebt({ name: "", balance: "", interest_rate: "", minimum_payment: "", account_id: "", debt_type: "credit_card" });
      setShowDebtForm(false);
    } catch(e) { console.error(e); }
  };

  const handleDeleteDebt = async (id) => {
    try {
      await deleteDebt(id);
      setDebts(prev => prev.filter(d => d.id !== id));
    } catch(e) { console.error(e); }
  };

  const fmt = (n) => `$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const inputStyle = { width: "100%", padding: "9px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" };

  const debtPct   = 100 - focus;
  const savingsPct = focus;
  const focusLabel = focus <= 20 ? "Aggressive Debt Payoff" : focus <= 40 ? "Debt Focus" : focus <= 60 ? "Balanced" : focus <= 80 ? "Savings Focus" : "Aggressive Savings";
  const focusColor = focus <= 30 ? COLORS.red : focus <= 60 ? COLORS.amber : COLORS.green;

  if (loading) return <div style={{ color: COLORS.textMuted, fontSize: 13, padding: "20px 0", textAlign: "center" }}>Loading...</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

      {/* Financial Focus Slider */}
      <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
        <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 14, margin: "0 0 6px" }}>Financial Focus</h3>
        <p style={{ color: COLORS.textMuted, fontSize: 12, margin: "0 0 18px" }}>Drag to set how you want to split extra money between paying off debt and building savings.</p>

        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ color: COLORS.red, fontSize: 12, fontWeight: 700 }}>Debt {debtPct}%</span>
          <span style={{ color: focusColor, fontSize: 13, fontWeight: 700 }}>{focusLabel}</span>
          <span style={{ color: COLORS.green, fontSize: 12, fontWeight: 700 }}>Savings {savingsPct}%</span>
        </div>

        <input
          type="range" min="0" max="100" step="5" value={focus}
          onChange={e => handleFocusChange(parseInt(e.target.value))}
          style={{ width: "100%", accentColor: focusColor, cursor: "pointer" }}
        />

        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 6 }}>Debt Strategy</div>
            <select value={strategy} onChange={e => setStrategy(e.target.value)} style={{ ...inputStyle }}>
              <option value="avalanche">Avalanche (highest interest first)</option>
              <option value="snowball">Snowball (smallest balance first)</option>
            </select>
          </div>
        </div>

        <button onClick={handleFocusSave} disabled={focusSaving} style={{ marginTop: 12, width: "100%", padding: "10px", background: COLORS.accentGlow, border: `1px solid ${COLORS.accent}44`, borderRadius: 8, color: COLORS.accent, cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 13 }}>
          {focusSaving ? "Saving..." : "Save Focus Settings"}
        </button>
      </div>

      {/* Savings Goals */}
      <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 14, margin: 0 }}>Savings Goals</h3>
          <button onClick={() => setShowGoalForm(v => !v)} style={{ padding: "5px 12px", background: COLORS.accentGlow, border: `1px solid ${COLORS.accent}44`, borderRadius: 8, color: COLORS.accent, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 12 }}>
            {showGoalForm ? "Cancel" : "+ Add Goal"}
          </button>
        </div>

        {showGoalForm && (
          <div style={{ background: COLORS.bg, borderRadius: 10, padding: "14px", marginBottom: 14, display: "flex", flexDirection: "column", gap: 8 }}>
            <input placeholder="Goal name (e.g. Vacation Fund)" value={newGoal.name} onChange={e => setNewGoal(p => ({ ...p, name: e.target.value }))} style={inputStyle} />
            <div style={{ display: "flex", gap: 8 }}>
              <input placeholder="Target amount" type="number" value={newGoal.target_amount} onChange={e => setNewGoal(p => ({ ...p, target_amount: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
              <input placeholder="Target date" type="date" value={newGoal.target_date} onChange={e => setNewGoal(p => ({ ...p, target_date: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
            </div>
            <select value={newGoal.account_id} onChange={e => setNewGoal(p => ({ ...p, account_id: e.target.value }))} style={inputStyle}>
              <option value="">Link to account (optional)</option>
              {savingsAccounts.map(a => <option key={a.id} value={a.id}>{a.custom_name || a.name}</option>)}
            </select>
            <button onClick={handleAddGoal} style={{ padding: "9px", background: COLORS.accent, border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 13 }}>Add Goal</button>
          </div>
        )}

        {goals.length === 0 && !showGoalForm && (
          <p style={{ color: COLORS.textMuted, fontSize: 13, textAlign: "center", padding: "12px 0" }}>No savings goals yet — add one above</p>
        )}

        {goals.map((goal, i) => {
          const balance = parseFloat(goal.account_balance || 0);
          const target  = parseFloat(goal.target_amount || 0);
          const p       = target > 0 ? Math.min(100, Math.round((balance / target) * 100)) : null;
          const barColor = p >= 100 ? COLORS.green : p >= 60 ? COLORS.accent : COLORS.amber;
          return (
            <div key={goal.id} style={{ paddingBottom: i < goals.length - 1 ? 14 : 0, marginBottom: i < goals.length - 1 ? 14 : 0, borderBottom: i < goals.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                <div>
                  <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 700 }}>{goal.name}</div>
                  {goal.target_date && <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2 }}>Target: {new Date(goal.target_date).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</div>}
                  {goal.account_name && <div style={{ color: COLORS.textMuted, fontSize: 11 }}>{goal.account_name}</div>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {target > 0 && <span style={{ color: COLORS.green, fontFamily: "monospace", fontSize: 13, fontWeight: 700 }}>{fmt(balance)} / {fmt(target)}</span>}
                  <button onClick={() => handleDeleteGoal(goal.id)} style={{ background: "none", border: "none", color: COLORS.textMuted, cursor: "pointer", fontSize: 16, padding: 0 }}>×</button>
                </div>
              </div>
              {p !== null && (
                <div>
                  <div style={{ height: 5, background: COLORS.border, borderRadius: 99, overflow: "hidden", marginBottom: 4 }}>
                    <div style={{ width: `${p}%`, height: "100%", background: barColor, borderRadius: 99 }} />
                  </div>
                  <span style={{ color: barColor, fontSize: 11, fontWeight: 700 }}>{p}% complete</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Debt Accounts */}
      <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 14, margin: 0 }}>Debt Accounts</h3>
          <button onClick={() => setShowDebtForm(v => !v)} style={{ padding: "5px 12px", background: COLORS.accentGlow, border: `1px solid ${COLORS.accent}44`, borderRadius: 8, color: COLORS.accent, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 12 }}>
            {showDebtForm ? "Cancel" : "+ Add Debt"}
          </button>
        </div>

        {showDebtForm && (
          <div style={{ background: COLORS.bg, borderRadius: 10, padding: "14px", marginBottom: 14, display: "flex", flexDirection: "column", gap: 8 }}>
            <input placeholder="Debt name (e.g. Chase Sapphire)" value={newDebt.name} onChange={e => setNewDebt(p => ({ ...p, name: e.target.value }))} style={inputStyle} />
            <select value={newDebt.debt_type} onChange={e => setNewDebt(p => ({ ...p, debt_type: e.target.value }))} style={inputStyle}>
              <option value="credit_card">Credit Card</option>
              <option value="auto_loan">Auto Loan</option>
              <option value="student_loan">Student Loan</option>
              <option value="personal_loan">Personal Loan</option>
              <option value="other">Other</option>
            </select>
            <div style={{ display: "flex", gap: 8 }}>
              <input placeholder="Current balance" type="number" value={newDebt.balance} onChange={e => setNewDebt(p => ({ ...p, balance: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
              <input placeholder="Interest rate %" type="number" value={newDebt.interest_rate} onChange={e => setNewDebt(p => ({ ...p, interest_rate: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
            </div>
            <input placeholder="Minimum monthly payment" type="number" value={newDebt.minimum_payment} onChange={e => setNewDebt(p => ({ ...p, minimum_payment: e.target.value }))} style={inputStyle} />
            <select value={newDebt.account_id} onChange={e => setNewDebt(p => ({ ...p, account_id: e.target.value }))} style={inputStyle}>
              <option value="">Link to Plaid account (optional)</option>
              {creditAccounts.map(a => <option key={a.id} value={a.id}>{a.custom_name || a.name}</option>)}
            </select>
            <button onClick={handleAddDebt} style={{ padding: "9px", background: COLORS.accent, border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 13 }}>Add Debt</button>
          </div>
        )}

        {debts.length === 0 && !showDebtForm && (
          <p style={{ color: COLORS.textMuted, fontSize: 13, textAlign: "center", padding: "12px 0" }}>No debts added yet</p>
        )}

        {debts.map((debt, i) => {
          const balance = parseFloat(debt.live_balance || debt.balance || 0);
          const rate    = parseFloat(debt.interest_rate || 0);
          const minPay  = parseFloat(debt.minimum_payment || 0);
          return (
            <div key={debt.id} style={{ paddingBottom: i < debts.length - 1 ? 14 : 0, marginBottom: i < debts.length - 1 ? 14 : 0, borderBottom: i < debts.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 700 }}>{debt.name}</div>
                  <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2, textTransform: "capitalize" }}>{debt.debt_type?.replace("_", " ")}{rate > 0 ? ` · ${rate}% APR` : ""}</div>
                  {minPay > 0 && <div style={{ color: COLORS.textMuted, fontSize: 11 }}>Min payment: {fmt(minPay)}/mo</div>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ color: COLORS.red, fontFamily: "monospace", fontSize: 14, fontWeight: 700 }}>-{fmt(balance)}</span>
                  <button onClick={() => handleDeleteDebt(debt.id)} style={{ background: "none", border: "none", color: COLORS.textMuted, cursor: "pointer", fontSize: 16, padding: 0 }}>×</button>
                </div>
              </div>
            </div>
          );
        })}

        {debts.length > 0 && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: COLORS.textMuted, fontSize: 12, fontWeight: 700 }}>Total Debt</span>
            <span style={{ color: COLORS.red, fontFamily: "monospace", fontSize: 14, fontWeight: 700 }}>-{fmt(debts.reduce((s,d) => s + parseFloat(d.live_balance || d.balance || 0), 0))}</span>
          </div>
        )}
      </div>

    </div>
  );
};

const WEEKDAY_INDEX = {
  Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3,
  Thursday: 4, Friday: 5, Saturday: 6
};

const countWeekdayOccurrences = (weekday, start, end) => {
  const targetDay = WEEKDAY_INDEX[weekday];
  if (targetDay === undefined) return 4;
  let count = 0;
  const d = new Date(start);
  while (d.getDay() !== targetDay) d.setDate(d.getDate() + 1);
  while (d <= end) { count++; d.setDate(d.getDate() + 7); }
  return count;
};

const periodAmount = (src) => {
  const amt = parseFloat(src.amount || 0);
  const { start, end } = getCurrentPeriod();
  if (src.schedule_type === 'monthly') return amt;
  if (src.schedule_type === 'twiceMonthly') return amt * 2;
  if (src.schedule_type === 'weekly') return amt * countWeekdayOccurrences(src.week_day, start, end);
  if (src.schedule_type === 'biweekly') return amt * Math.ceil(countWeekdayOccurrences(src.week_day, start, end) / 2);
  return amt;
};

const periodLabel = () => {
  const { start, end } = getCurrentPeriod();
  const f = (d) => d.toLocaleString('default', { month: 'short', day: 'numeric' });
  return `${f(start)} – ${f(end)}`;
};

const getInputStyle = (COLORS) => ({
  width: "100%", padding: "10px 12px", background: COLORS.bg,
  border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text,
  fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box",
});

const getTypeColor = (COLORS) => ({ fixed: COLORS.accent, variable: COLORS.green, misc: COLORS.amber, cc_payment: COLORS.textMuted });

const PLAID_CATEGORIES = [
  { key: 'FOOD_AND_DRINK',            label: 'Food & Drink' },
  { key: 'GROCERIES',                 label: 'Groceries' },
  { key: 'TRANSPORTATION',            label: 'Transportation' },
  { key: 'TRAVEL',                    label: 'Travel' },
  { key: 'ENTERTAINMENT',             label: 'Entertainment' },
  { key: 'PERSONAL_CARE',             label: 'Personal Care' },
  { key: 'MEDICAL',                   label: 'Medical' },
  { key: 'SHOPPING',                  label: 'Shopping' },
  { key: 'GENERAL_MERCHANDISE',       label: 'General Merchandise' },
  { key: 'RENT_AND_UTILITIES',        label: 'Rent & Utilities' },
  { key: 'LOAN_PAYMENTS',             label: 'Loan / CC Payments' },
  { key: 'TRANSFER_IN',               label: 'Transfer In' },
  { key: 'TRANSFER_OUT',              label: 'Transfer Out' },
  { key: 'INCOME',                    label: 'Income' },
  { key: 'GOVERNMENT_AND_NON_PROFIT', label: 'Government / Non-profit' },
  { key: 'HOME_IMPROVEMENT',          label: 'Home Improvement' },
  { key: 'GENERAL_SERVICES',          label: 'General Services' },
];

// ─── Plaid Link Button ────────────────────────────────────────────────────────
const PlaidLinkButton = ({ onSuccess }) => {
  const { COLORS } = useTheme();
  const [linkToken,    setLinkToken]    = useState(null);
  const [loadingToken, setLoadingToken] = useState(false);
  const [error,        setError]        = useState(null);

  useEffect(() => {
    const fetchToken = async () => {
      setLoadingToken(true); setError(null);
      try {
        const res   = await createLinkToken();
        const token = res.data?.link_token || res.data?.data?.link_token;
        if (!token) throw new Error('No link token returned');
        setLinkToken(token);
      } catch (err) {
        console.error('Failed to get link token:', err);
        setError('Could not load Plaid. Try again.');
      } finally { setLoadingToken(false); }
    };
    fetchToken();
  }, []);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (public_token) => {
      try { await exchangeToken(public_token); onSuccess(); }
      catch (err) { console.error('Failed to exchange token:', err); }
    },
    onExit: (err) => { if (err) console.error('Plaid Link exited with error:', err); },
  });

  if (error) return <p style={{ color: COLORS.red, fontSize: 12, margin: 0 }}>{error}</p>;

  return (
    <button onClick={() => open()} disabled={!ready || loadingToken}
      style={{ width: "100%", padding: "12px", marginTop: 12, background: ready ? COLORS.accentGlow : "transparent", border: `1px solid ${ready ? COLORS.accent : COLORS.border}`, borderRadius: 10, color: ready ? COLORS.accent : COLORS.textMuted, cursor: ready ? "pointer" : "not-allowed", fontFamily: "inherit", fontWeight: 700, fontSize: 14 }}>
      {loadingToken ? "Preparing Plaid..." : !ready ? "Loading..." : "+ Connect a Bank Account"}
    </button>
  );
};

// ─── Plaid Reconnect Button ───────────────────────────────────────────────────
const PlaidReconnectButton = ({ itemId, onSuccess }) => {
  const { COLORS } = useTheme();
  const [linkToken,    setLinkToken]    = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [ready,        setReady]        = useState(false);

  const fetchUpdateToken = async () => {
    setLoading(true);
    try {
      const res   = await createUpdateLinkToken(itemId);
      const token = res.data?.link_token || res.data?.data?.link_token;
      if (!token) throw new Error('No update link token returned');
      setLinkToken(token);
    } catch (err) {
      console.error('Failed to get update link token:', err);
      setLoading(false);
    }
  };

  const { open, ready: plaidReady } = usePlaidLink({
    token: linkToken,
    onSuccess: async () => {
      try { onSuccess(); }
      catch (err) { console.error('Reconnect error:', err); }
    },
    onExit: (err) => { if (err) console.error('Plaid reconnect exited:', err); },
  });

  // Once we have a token and Plaid is ready, auto-open
  useEffect(() => {
    if (plaidReady && linkToken && loading) {
      setLoading(false);
      setReady(true);
      open();
    }
  }, [plaidReady, linkToken]);

  return (
    <button
      onClick={fetchUpdateToken}
      disabled={loading}
      style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${COLORS.amber}66`, borderRadius: 8, color: COLORS.amber, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 12 }}
    >
      {loading ? "Preparing..." : "⚠️ Fix Connection"}
    </button>
  );
};

// ─── Category Row with inline edit ───────────────────────────────────────────
const CategoryRow = ({ cat, isLast, onDelete, onUpdate }) => {
  const { COLORS } = useTheme();
  const inputStyle = getInputStyle(COLORS);
  const TYPE_COLOR = getTypeColor(COLORS);
  const [editing, setEditing] = useState(false);
  const [form,    setForm]    = useState({ name: cat.name, amount: cat.amount, type: cat.type, due_day: cat.due_day || "" });
  const [saving,  setSaving]  = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(cat.id, { name: form.name, amount: parseFloat(form.amount), type: form.type, due_day: form.due_day ? parseInt(form.due_day) : null });
      setEditing(false);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (e) => {
    e.stopPropagation();
    try { await onUpdate(cat.id, { is_active: !cat.is_active }); }
    catch (err) { console.error(err); }
  };

  return (
    <div style={{ borderBottom: isLast ? "none" : `1px solid ${COLORS.border}`, opacity: cat.is_active === false ? 0.5 : 1, transition: "opacity 0.2s" }}>
      {!editing ? (
        <div style={{ display: "flex", alignItems: "center", padding: "12px 0", gap: 12 }}>
          <div style={{ flex: 1, cursor: "pointer" }} onClick={() => setEditing(true)}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 2, flexWrap: "wrap" }}>
              <span style={{ color: COLORS.text, fontWeight: 600, fontSize: 14 }}>{cat.name}</span>
              <Badge label={cat.type} color={TYPE_COLOR[cat.type] || COLORS.textMuted} />
              {cat.is_active === false && <Badge label="inactive" color={COLORS.textMuted} />}
            </div>
            {cat.due_day && <span style={{ color: COLORS.textMuted, fontSize: 11 }}>Due day {cat.due_day}</span>}
          </div>
          <span style={{ color: COLORS.green, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 14 }}>{fmt(cat.amount)}</span>
          <button onClick={handleToggleActive} title={cat.is_active !== false ? "Set inactive" : "Set active"}
            style={{ background: "transparent", border: `1px solid ${cat.is_active !== false ? COLORS.green + '44' : COLORS.border}`, borderRadius: 6, color: cat.is_active !== false ? COLORS.green : COLORS.textMuted, cursor: "pointer", fontSize: 11, padding: "3px 7px", fontFamily: "inherit", fontWeight: 700, flexShrink: 0 }}>
            {cat.is_active !== false ? "On" : "Off"}
          </button>
          <button onClick={() => onDelete(cat.id)} style={{ background: "transparent", border: "none", color: COLORS.textMuted, cursor: "pointer", fontSize: 17, padding: 0, opacity: 0.6 }}>×</button>
        </div>
      ) : (
        <div style={{ padding: "12px 0 16px", display: "flex", flexDirection: "column", gap: 8 }}>
          <input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Category name" style={inputStyle} />
          <input value={form.amount} onChange={e => setForm(p => ({...p, amount: e.target.value}))} placeholder="Monthly amount ($)" type="number" style={inputStyle} />
          <div style={{ display: "flex", gap: 6 }}>
            {["fixed", "variable", "misc"].map(t => (
              <button key={t} onClick={() => setForm(p => ({...p, type: t}))}
                style={{ flex: 1, padding: "8px", borderRadius: 8, border: `1px solid ${form.type === t ? COLORS.accent : COLORS.border}`, background: form.type === t ? COLORS.accentGlow : "transparent", color: form.type === t ? COLORS.accent : COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 12, textTransform: "capitalize" }}>
                {t}
              </button>
            ))}
          </div>
          {form.type === "fixed" && (
            <input value={form.due_day} onChange={e => setForm(p => ({...p, due_day: e.target.value}))} placeholder="Due day (1-31)" type="number" style={inputStyle} />
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setEditing(false)} style={{ flex: 1, padding: "9px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 13 }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: "9px", background: COLORS.accent, border: "none", borderRadius: 8, color: "#fff", cursor: saving ? "not-allowed" : "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 13 }}>
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Income Row with inline edit ─────────────────────────────────────────────
const IncomeRow = ({ src, isLast, onDelete, onUpdate }) => {
  const { COLORS } = useTheme();
  const inputStyle = getInputStyle(COLORS);
  const [editing, setEditing] = useState(false);
  const [form,    setForm]    = useState({
    name: src.name, amount: src.amount,
    schedule_type: src.schedule_type || 'monthly',
    month_day:   src.month_day   || '',
    month_day_1: src.month_day_1 || '',
    month_day_2: src.month_day_2 || '',
    week_day:    src.week_day    || 'Monday',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(src.id, {
        name:         form.name,
        amount:       parseFloat(form.amount),
        schedule_type: form.schedule_type,
        month_day:    form.schedule_type === 'monthly' && form.month_day ? parseInt(form.month_day) : null,
        month_day_1:  form.schedule_type === 'twiceMonthly' && form.month_day_1 ? parseInt(form.month_day_1) : null,
        month_day_2:  form.schedule_type === 'twiceMonthly' && form.month_day_2 ? parseInt(form.month_day_2) : null,
        week_day:     (form.schedule_type === 'weekly' || form.schedule_type === 'biweekly') ? form.week_day : null,
      });
      setEditing(false);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ borderBottom: isLast ? "none" : `1px solid ${COLORS.border}` }}>
      {!editing ? (
        <div style={{ display: "flex", alignItems: "center", padding: "12px 0", gap: 12 }}>
          <div style={{ flex: 1, cursor: "pointer" }} onClick={() => setEditing(true)}>
            <div style={{ color: COLORS.text, fontWeight: 600, fontSize: 14 }}>{src.name}</div>
            <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2 }}>{scheduleLabel(src)}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ color: COLORS.green, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 14 }}>+{fmt(periodAmount(src))}</div>
            <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 1 }}>{fmt(parseFloat(src.amount))} / paycheck</div>
          </div>
          <button onClick={() => onDelete(src.id)} style={{ background: "transparent", border: "none", color: COLORS.textMuted, cursor: "pointer", fontSize: 17, padding: 0, opacity: 0.6 }}>×</button>
        </div>
      ) : (
        <div style={{ padding: "12px 0 16px", display: "flex", flexDirection: "column", gap: 8 }}>
          <input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Source name" style={inputStyle} />
          <input value={form.amount} onChange={e => setForm(p => ({...p, amount: e.target.value}))} placeholder="Amount per paycheck ($)" type="number" style={inputStyle} />
          <div style={{ display: "flex", gap: 6 }}>
            {["weekly", "biweekly", "monthly", "twiceMonthly", "misc"].map(t => (
              <button key={t} onClick={() => setForm(p => ({...p, schedule_type: t}))}
                style={{ flex: 1, padding: "7px 4px", borderRadius: 8, border: `1px solid ${form.schedule_type === t ? COLORS.accent : COLORS.border}`, background: form.schedule_type === t ? COLORS.accentGlow : "transparent", color: form.schedule_type === t ? COLORS.accent : COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 10 }}>
                {t === "twiceMonthly" ? "2x/mo" : t}
              </button>
            ))}
          </div>
          {form.schedule_type === "monthly" && (
            <input value={form.month_day} onChange={e => setForm(p => ({...p, month_day: e.target.value}))} placeholder="Day of month (1-31)" type="number" style={inputStyle} />
          )}
          {form.schedule_type === "twiceMonthly" && (
            <div style={{ display: "flex", gap: 8 }}>
              <input value={form.month_day_1} onChange={e => setForm(p => ({...p, month_day_1: e.target.value}))} placeholder="1st date" type="number" style={{ ...inputStyle, flex: 1 }} />
              <input value={form.month_day_2} onChange={e => setForm(p => ({...p, month_day_2: e.target.value}))} placeholder="2nd date" type="number" style={{ ...inputStyle, flex: 1 }} />
            </div>
          )}
          {(form.schedule_type === "weekly" || form.schedule_type === "biweekly") && (
            <select value={form.week_day} onChange={e => setForm(p => ({...p, week_day: e.target.value}))} style={inputStyle}>
              {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map(d => <option key={d}>{d}</option>)}
            </select>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setEditing(false)} style={{ flex: 1, padding: "9px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 13 }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: "9px", background: COLORS.accent, border: "none", borderRadius: 8, color: "#fff", cursor: saving ? "not-allowed" : "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 13 }}>
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Settings ────────────────────────────────────────────────────────────
const Settings = () => {
  const { COLORS, themeKey, setTheme, THEMES } = useTheme();
  const inputStyle = getInputStyle(COLORS);
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState("budget");

  const [categories,    setCategories]    = useState([]);
  const [incomes,       setIncomes]       = useState([]);
  const [accounts,      setAccounts]      = useState([]);
  const [household,     setHousehold]     = useState(null);
  const [plaidMappings, setPlaidMappings] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showAllExpenses, setShowAllExpenses] = useState(false);

  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [newExp, setNewExp] = useState({ name: "", amount: "", type: "fixed", due_day: "" });

  const [showIncomeForm, setShowIncomeForm] = useState(false);
  const [newInc, setNewInc] = useState({ name: "", amount: "", schedule_type: "monthly", month_day: "", month_day_1: "", month_day_2: "", week_day: "Monday" });

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteMsg,   setInviteMsg]   = useState("");
  const [syncing,      setSyncing]      = useState(false);
  const [periodSaving, setPeriodSaving] = useState(false);
  const [periodDraft,  setPeriodDraft]  = useState(null);
  const [banks,        setBanks]        = useState([]);
  const [disconnecting, setDisconnecting] = useState(null); // item_id being disconnected
  const [googleSyncing,    setGoogleSyncing]    = useState(false);
  const [googleSyncStatus, setGoogleSyncStatus] = useState(null);
  const [priorities,       setPriorities]       = useState([]);
  const [prioritySaving,   setPrioritySaving]   = useState(false);

  const unwrapArray  = (res) => Array.isArray(res.data) ? res.data : res.data?.data || [];
  const unwrapObject = (res) => Array.isArray(res.data) ? res.data : res.data?.data || res.data || null;

  const loadAccounts = useCallback(async () => {
    const r = await getAccounts();
    setAccounts(unwrapArray(r));
    try { const b = await getPlaidBanks(); setBanks(Array.isArray(b.data) ? b.data : b.data?.data || []); } catch(e) {}
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [catRes, incRes, accRes, hhRes, mapRes, bankRes] = await Promise.all([
          getCategories(), getIncome(), getAccounts(), getHousehold(), getPlaidMappings(), getPlaidBanks()
        ]);
        setCategories(unwrapArray(catRes));
        setIncomes(unwrapArray(incRes));
        setAccounts(unwrapArray(accRes));
        const hh = unwrapObject(hhRes);
        setHousehold(hh);
        setPeriodDraft({ month_start: hh?.month_start || 26, week_start: hh?.week_start || 'Sunday' });
        setPlaidMappings(unwrapArray(mapRes));
        setBanks(Array.isArray(bankRes.data) ? bankRes.data : bankRes.data?.data || []);
        try { const gs = await getGoogleSyncStatus(); setGoogleSyncStatus(gs.data); } catch(e) {}
        try { const pr = await getPriorities(); setPriorities(pr.data?.priorities || []); } catch(e) {}
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleAddCategory = async () => {
    if (!newExp.name || (!newExp.amount && newExp.type !== 'misc')) return;
    try {
      const res = await addCategory({ name: newExp.name, amount: newExp.type === 'misc' ? 0 : parseFloat(newExp.amount), type: newExp.type, due_day: newExp.due_day ? parseInt(newExp.due_day) : null });
      setCategories(p => [...p, res.data?.data || res.data]);
      setNewExp({ name: "", amount: "", type: "fixed", due_day: "" });
      setShowExpenseForm(false);
    } catch (err) { console.error(err); }
  };

  const handleUpdateCategory = async (id, data) => {
    const res = await updateCategory(id, data);
    const updated = res.data?.data || res.data;
    setCategories(p => p.map(c => c.id === id ? { ...c, ...updated } : c));
  };

  const handleDeleteCategory = async (id) => {
    try { await deleteCategory(id); setCategories(p => p.filter(c => c.id !== id)); }
    catch (err) { console.error(err); }
  };

  const handleAddIncome = async () => {
    if (!newInc.name || (!newInc.amount && newInc.schedule_type !== 'misc')) return;
    try {
      const res = await addIncome({ name: newInc.name, amount: newInc.schedule_type === 'misc' ? 0 : parseFloat(newInc.amount), schedule_type: newInc.schedule_type, month_day: newInc.schedule_type === 'monthly' && newInc.month_day ? parseInt(newInc.month_day) : null, month_day_1: newInc.schedule_type === 'twiceMonthly' && newInc.month_day_1 ? parseInt(newInc.month_day_1) : null, month_day_2: newInc.schedule_type === 'twiceMonthly' && newInc.month_day_2 ? parseInt(newInc.month_day_2) : null, week_day: newInc.week_day || null });
      setIncomes(p => [...p, res.data?.data || res.data]);
      setNewInc({ name: "", amount: "", schedule_type: "monthly", month_day: "", month_day_1: "", month_day_2: "", week_day: "Monday" });
      setShowIncomeForm(false);
    } catch (err) { console.error(err); }
  };

  const handleUpdateIncome = async (id, data) => {
    const res = await updateIncome(id, data);
    const updated = res.data?.data || res.data;
    setIncomes(p => p.map(s => s.id === id ? { ...s, ...updated } : s));
  };

  const handleDeleteIncome = async (id) => {
    try { await deleteIncome(id); setIncomes(p => p.filter(i => i.id !== id)); }
    catch (err) { console.error(err); }
  };

  const handleInvite = async () => {
    try {
      await inviteMember(inviteEmail);
      setInviteMsg(`${inviteEmail} added to household`);
      setInviteEmail("");
      setHousehold(unwrapObject(await getHousehold()));
    } catch (err) { setInviteMsg(err.response?.data?.error || 'Something went wrong'); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try { await syncPlaid(); await loadAccounts(); }
    catch (err) { console.error(err); }
    finally { setSyncing(false); }
  };

  const handleGoogleSync = async () => {
    setGoogleSyncing(true);
    try {
      await syncGoogleSheets(true);
      const gs = await getGoogleSyncStatus();
      setGoogleSyncStatus(gs.data);
    } catch (err) { console.error(err); }
    finally { setGoogleSyncing(false); }
  };

  const handleDisconnect = async (itemId, bankAccounts) => {
    const names = bankAccounts.map(a => a.name).join(', ');
    if (!window.confirm(`Disconnect this bank?\n\nAccounts: ${names}\n\nThis will permanently delete all transactions for these accounts.`)) return;
    setDisconnecting(itemId);
    try {
      await disconnectBank(itemId);
      await loadAccounts();
    } catch (err) { console.error(err); }
    finally { setDisconnecting(null); }
  };

  const handleUpdatePeriod = async () => {
    setPeriodSaving(true);
    try {
      const res = await updateHousehold({ month_start: parseInt(periodDraft.month_start), week_start: periodDraft.week_start });
      const updated = res.data?.data || res.data;
      setHousehold(prev => ({ ...prev, ...updated }));
    } catch (err) { console.error(err); }
    finally { setPeriodSaving(false); }
  };

  const handleToggleSavings = async (acc) => {
    try {
      await updateAccount(acc.id, { show_as_savings: !acc.show_as_savings });
      setAccounts(prev => prev.map(a => a.id === acc.id ? { ...a, show_as_savings: !a.show_as_savings } : a));
    } catch (err) { console.error(err); }
  };

  const handleMappingChange = async (plaidCategory, value) => {
    const isTransfer        = value === '__transfer__';
    const expenseCategoryId = isTransfer || value === '' ? null : parseInt(value);
    try {
      const res     = await updatePlaidMapping({ plaid_category: plaidCategory, expense_category_id: expenseCategoryId, is_transfer: isTransfer });
      const updated = res.data?.data || res.data;
      setPlaidMappings(prev => {
        const exists = prev.find(m => m.plaid_category === plaidCategory);
        if (exists) return prev.map(m => m.plaid_category === plaidCategory ? { ...m, ...updated } : m);
        return [...prev, updated];
      });
    } catch (err) { console.error(err); }
  };

  const getMappingValue = (plaidKey) => {
    const m = plaidMappings.find(m => m.plaid_category === plaidKey);
    if (!m) return '';
    if (m.is_transfer) return '__transfer__';
    return m.expense_category_id ? String(m.expense_category_id) : '';
  };

  const activeCategories   = categories.filter(c => c.is_active !== false);
  const inactiveCategories = categories.filter(c => c.is_active === false);
  const displayedActive    = showAllExpenses ? activeCategories : activeCategories.slice(0, 8);

  const TABS = ["budget", "income", "mapping", "accounts", "goals", "team", "appearance"];

  if (loading) return (
    <div style={{ padding: 24, color: COLORS.textMuted, textAlign: 'center', paddingTop: 80 }}>Loading...</div>
  );

  return (
    <div style={{ padding: "0 0 100px", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ padding: "20px 16px 14px" }}>
        <h2 style={{ color: COLORS.text, fontSize: 22, fontWeight: 800, margin: 0, letterSpacing: -0.5 }}>Settings</h2>
      </div>

      {/* Tab bar */}
      <div style={{ padding: "0 12px 14px", display: "flex", gap: 6, overflowX: "auto", scrollbarWidth: "none" }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setActiveTab(t)} style={{ flexShrink: 0, padding: "8px 16px", borderRadius: 20, border: `1px solid ${activeTab === t ? COLORS.accent : COLORS.border}`, background: activeTab === t ? COLORS.accentGlow : "transparent", color: activeTab === t ? COLORS.accent : COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 13, textTransform: "capitalize" }}>
            {t === "budget" ? "💰 Budget" : t === "income" ? "💵 Income" : t === "mapping" ? "🗂 Mapping" : t === "accounts" ? "🏦 Accounts" : t === "goals" ? "🎯 Goals" : t === "team" ? "👥 Team" : "🎨 Appearance"}
          </button>
        ))}
      </div>

      <div style={{ padding: "0 12px" }}>

        {/* ── Budget tab ── */}
        {activeTab === "budget" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

            {/* Period Settings */}
            <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
              <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 14, margin: "0 0 4px" }}>Billing Period</h3>
              <p style={{ color: COLORS.textMuted, fontSize: 12, margin: "0 0 14px" }}>
                Current period: {periodDraft ? (() => { const { start, end } = getCurrentPeriod(periodDraft.month_start); return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`; })() : '—'}
              </p>
              <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 6 }}>Month Starts On</div>
                  <select
                    value={periodDraft?.month_start || 26}
                    onChange={e => setPeriodDraft(p => ({ ...p, month_start: parseInt(e.target.value) }))}
                    style={{ ...inputStyle }}
                  >
                    {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                      <option key={d} value={d}>Day {d}</option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 6 }}>Week Starts On</div>
                  <select
                    value={periodDraft?.week_start || 'Sunday'}
                    onChange={e => setPeriodDraft(p => ({ ...p, week_start: e.target.value }))}
                    style={{ ...inputStyle }}
                  >
                    {["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"].map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                onClick={handleUpdatePeriod}
                disabled={periodSaving}
                style={{ width: "100%", padding: "10px", background: COLORS.accent, border: "none", borderRadius: 8, color: "#fff", cursor: periodSaving ? "not-allowed" : "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 14 }}
              >
                {periodSaving ? "Saving..." : "Save Period Settings"}
              </button>
            </div>
            <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div>
                  <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 14, margin: 0 }}>Expense Categories</h3>
                  <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2 }}>Tap a category to edit</div>
                </div>
                <button onClick={() => setShowExpenseForm(v => !v)} style={{ padding: "6px 12px", background: COLORS.accentGlow, border: `1px solid ${COLORS.accent}44`, borderRadius: 8, color: COLORS.accent, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 13 }}>
                  {showExpenseForm ? "Cancel" : "+ Add"}
                </button>
              </div>

              {showExpenseForm && (
                <div style={{ marginBottom: 16, padding: 14, background: COLORS.bg, borderRadius: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                  <input value={newExp.name} onChange={e => setNewExp(p => ({...p, name: e.target.value}))} placeholder="Category name" style={inputStyle} />
                  {newExp.type !== "misc" && (
                  <input value={newExp.amount} onChange={e => setNewExp(p => ({...p, amount: e.target.value}))} placeholder="Monthly amount ($)" type="number" style={inputStyle} />
                  )}
                  <div style={{ display: "flex", gap: 6 }}>
                    {["fixed", "variable", "misc"].map(t => (
                      <button key={t} onClick={() => setNewExp(p => ({...p, type: t}))} style={{ flex: 1, padding: "8px", borderRadius: 8, border: `1px solid ${newExp.type === t ? COLORS.accent : COLORS.border}`, background: newExp.type === t ? COLORS.accentGlow : "transparent", color: newExp.type === t ? COLORS.accent : COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 12, textTransform: "capitalize" }}>{t}</button>
                    ))}
                  </div>
                  {newExp.type === "fixed" && (
                    <input value={newExp.due_day} onChange={e => setNewExp(p => ({...p, due_day: e.target.value}))} placeholder="Due day (1-31)" type="number" style={inputStyle} />
                  )}
                  <button onClick={handleAddCategory} style={{ padding: "10px", background: COLORS.accent, border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 14 }}>Add Category</button>
                </div>
              )}

              {categories.length === 0 && (
                <p style={{ color: COLORS.textMuted, fontSize: 13, textAlign: "center", padding: "20px 0" }}>No categories yet — add one above</p>
              )}

              {displayedActive.map((cat, i) => (
                <CategoryRow
                  key={cat.id} cat={cat}
                  isLast={i === displayedActive.length - 1 && inactiveCategories.length === 0}
                  onDelete={handleDeleteCategory}
                  onUpdate={handleUpdateCategory}
                />
              ))}

              {activeCategories.length > 8 && (
                <button onClick={() => setShowAllExpenses(v => !v)} style={{ width: "100%", marginTop: 12, padding: "9px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}>
                  {showAllExpenses ? "Show less" : `Show all ${activeCategories.length} active categories`}
                </button>
              )}

              {inactiveCategories.length > 0 && (
                <>
                  <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", margin: "14px 0 4px", paddingLeft: 2 }}>Inactive</div>
                  {inactiveCategories.map((cat, i) => (
                    <CategoryRow
                      key={cat.id} cat={cat}
                      isLast={i === inactiveCategories.length - 1}
                      onDelete={handleDeleteCategory}
                      onUpdate={handleUpdateCategory}
                    />
                  ))}
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Income tab ── */}
        {activeTab === "income" && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div>
                <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 14, margin: 0 }}>Income Sources</h3>
                <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 3 }}>{(() => { const { start, end } = getCurrentPeriod(household?.month_start || 26); return `${start.toLocaleDateString('default', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('default', { month: 'short', day: 'numeric' })}`; })()}</div>
              </div>
              <button onClick={() => setShowIncomeForm(v => !v)} style={{ padding: "6px 12px", background: COLORS.accentGlow, border: `1px solid ${COLORS.accent}44`, borderRadius: 8, color: COLORS.accent, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 13 }}>
                {showIncomeForm ? "Cancel" : "+ Add"}
              </button>
            </div>

            {showIncomeForm && (
              <div style={{ marginBottom: 16, padding: 14, background: COLORS.bg, borderRadius: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                <input value={newInc.name} onChange={e => setNewInc(p => ({...p, name: e.target.value}))} placeholder="Source name" style={inputStyle} />
                {newInc.schedule_type !== "misc" && (
                <input value={newInc.amount} onChange={e => setNewInc(p => ({...p, amount: e.target.value}))} placeholder="Amount per paycheck ($)" type="number" style={inputStyle} />
                )}
                <div style={{ display: "flex", gap: 6 }}>
                  {["weekly", "biweekly", "monthly", "twiceMonthly", "misc"].map(t => (
                    <button key={t} onClick={() => setNewInc(p => ({...p, schedule_type: t}))} style={{ flex: 1, padding: "7px 4px", borderRadius: 8, border: `1px solid ${newInc.schedule_type === t ? COLORS.accent : COLORS.border}`, background: newInc.schedule_type === t ? COLORS.accentGlow : "transparent", color: newInc.schedule_type === t ? COLORS.accent : COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 10 }}>
                      {t === "twiceMonthly" ? "2x/mo" : t}
                    </button>
                  ))}
                </div>
                {newInc.schedule_type === "monthly" && (
                  <input value={newInc.month_day} onChange={e => setNewInc(p => ({...p, month_day: e.target.value}))} placeholder="Day of month (1-31)" type="number" style={inputStyle} />
                )}
                {newInc.schedule_type === "twiceMonthly" && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <input value={newInc.month_day_1} onChange={e => setNewInc(p => ({...p, month_day_1: e.target.value}))} placeholder="1st date" type="number" style={{ ...inputStyle, flex: 1 }} />
                    <input value={newInc.month_day_2} onChange={e => setNewInc(p => ({...p, month_day_2: e.target.value}))} placeholder="2nd date" type="number" style={{ ...inputStyle, flex: 1 }} />
                  </div>
                )}
                {(newInc.schedule_type === "weekly" || newInc.schedule_type === "biweekly") && (
                  <select value={newInc.week_day} onChange={e => setNewInc(p => ({...p, week_day: e.target.value}))} style={inputStyle}>
                    {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map(d => <option key={d}>{d}</option>)}
                  </select>
                )}
                <button onClick={handleAddIncome} style={{ padding: "10px", background: COLORS.green, border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 14 }}>Add Income Source</button>
              </div>
            )}

            {incomes.length === 0 && <p style={{ color: COLORS.textMuted, fontSize: 13, textAlign: "center", padding: "20px 0" }}>No income sources yet</p>}

            <div style={{ color: COLORS.textMuted, fontSize: 11, marginBottom: 6 }}>Tap a source to edit</div>
            {incomes.map((src, i) => (
              <IncomeRow
                key={src.id} src={src}
                isLast={i === incomes.length - 1}
                onDelete={handleDeleteIncome}
                onUpdate={handleUpdateIncome}
              />
            ))}
          </div>
        )}

        {/* ── Mapping tab ── */}
        {activeTab === "mapping" && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 14, margin: "0 0 4px" }}>Auto-Categorize</h3>
              <p style={{ color: COLORS.textMuted, fontSize: 12, margin: 0, lineHeight: 1.5 }}>
                Map Plaid's transaction types to your expense categories. New transactions will be categorized automatically on sync.
              </p>
            </div>

            {PLAID_CATEGORIES.map((pc, i) => (
              <div key={pc.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0", borderBottom: i < PLAID_CATEGORIES.length - 1 ? `1px solid ${COLORS.border}` : "none", gap: 12 }}>
                <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 600, flexShrink: 0, width: "40%" }}>{pc.label}</div>
                <select
                  value={getMappingValue(pc.key)}
                  onChange={e => handleMappingChange(pc.key, e.target.value)}
                  style={{ ...inputStyle, flex: 1, fontSize: 12, padding: "7px 10px" }}
                >
                  <option value="">Uncategorized</option>
                  <option value="__transfer__">— Transfer —</option>
                  <optgroup label="Fixed">
                    {categories.filter(c => c.type === 'fixed').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </optgroup>
                  <optgroup label="Variable">
                    {categories.filter(c => c.type === 'variable').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </optgroup>
                  <optgroup label="Misc">
                    {categories.filter(c => c.type === 'misc').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </optgroup>
                  <optgroup label="Other">
                    {categories.filter(c => c.type === 'cc_payment').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </optgroup>
                </select>
              </div>
            ))}
          </div>
        )}

        {/* ── Accounts tab ── */}
        {activeTab === "accounts" && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 14, margin: 0 }}>Connected Accounts</h3>
              <button onClick={handleSync} disabled={syncing} style={{ padding: "6px 12px", background: COLORS.accentGlow, border: `1px solid ${COLORS.accent}44`, borderRadius: 8, color: COLORS.accent, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 13 }}>
                {syncing ? "Syncing..." : "⟳ Sync"}
              </button>
            </div>

            {accounts.filter(a => ['depository','credit'].includes(a.type)).length === 0 && (
              <p style={{ color: COLORS.textMuted, fontSize: 13, textAlign: "center", padding: "20px 0" }}>No accounts connected yet</p>
            )}

            {accounts.filter(a => ['depository','credit'].includes(a.type)).map((acc, i, arr) => (
              <div key={acc.id} style={{ borderBottom: i < arr.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: acc.last_synced ? COLORS.green : COLORS.amber, flexShrink: 0 }} />
                    <div>
                      <input
                        defaultValue={acc.custom_name || acc.name}
                        placeholder={acc.name}
                        onBlur={async (e) => {
                          const val = e.target.value.trim() || null;
                          if (val !== (acc.custom_name || null)) {
                            await updateAccount(acc.id, { custom_name: val });
                            setAccounts(prev => prev.map(a => a.id === acc.id ? { ...a, custom_name: val } : a));
                          }
                        }}
                        style={{ background: "transparent", border: "none", outline: "none", color: COLORS.text, fontSize: 13, fontFamily: "inherit", width: "100%", cursor: "text", padding: 0 }}
                      />
                      <div style={{ color: COLORS.textMuted, fontSize: 11, textTransform: "capitalize" }}>{acc.type} · {acc.subtype}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {acc.type === 'depository' && (
                      <button
                        onClick={() => handleToggleSavings(acc)}
                        title="Count as savings"
                        style={{ padding: "3px 8px", borderRadius: 6, border: `1px solid ${acc.show_as_savings ? COLORS.green : COLORS.border}`, background: acc.show_as_savings ? COLORS.greenGlow : "transparent", color: acc.show_as_savings ? COLORS.green : COLORS.textMuted, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                        🏦 Savings
                      </button>
                    )}
                    <div style={{ textAlign: "right" }}>
                      <div style={{ color: acc.type === 'credit' ? COLORS.red : COLORS.green, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 13 }}>
                        {acc.type === 'credit' ? '-' : ''}{fmt(Math.abs(parseFloat(acc.balance)))}
                      </div>
                      <div style={{ color: acc.last_synced ? COLORS.green : COLORS.amber, fontSize: 11 }}>{acc.last_synced ? "Synced" : "Manual"}</div>
                    </div>
                  </div>
                </div>

              </div>
            ))}

            <PlaidLinkButton onSuccess={handleSync} />

            {/* Connected Banks with disconnect */}
            {banks.length > 0 && (
              <div style={{ marginTop: 16, borderTop: `1px solid ${COLORS.border}`, paddingTop: 14 }}>
                <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 10 }}>Connected Banks</div>
                {banks.map((bank, i) => {
                  const hasError = bank.error === 'ITEM_LOGIN_REQUIRED' || !!bank.consent_expiration_time;
                  return (
                    <div key={bank.item_id} style={{ padding: "10px 0", borderBottom: i < banks.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 600 }}>
                              {bank.institution_name || bank.accounts?.[0]?.name?.split(' ').slice(0, 2).join(' ') || 'Bank'}
                            </div>
                          </div>
                          <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2 }}>
                            {bank.accounts?.length} account{bank.accounts?.length !== 1 ? 's' : ''} connected
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <PlaidReconnectButton itemId={bank.item_id} onSuccess={handleSync} />
                          <button
                            onClick={() => handleDisconnect(bank.item_id, bank.accounts || [])}
                            disabled={disconnecting === bank.item_id}
                            style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${COLORS.red}44`, borderRadius: 8, color: COLORS.red, cursor: disconnecting === bank.item_id ? "not-allowed" : "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 12 }}
                          >
                            {disconnecting === bank.item_id ? "Removing..." : "Disconnect"}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}



        {/* ── Google Drive Sync card (within Accounts tab) ── */}
        {activeTab === "accounts" && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px", marginTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <div>
                <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 14, margin: "0 0 4px" }}>📊 Google Drive Sync</h3>
                <p style={{ color: COLORS.textMuted, fontSize: 12, margin: 0 }}>
                  Exports transactions to a personal Google Sheet — one tab per budget period.
                </p>
              </div>
              {googleSyncStatus?.connected && (
                <button
                  onClick={handleGoogleSync}
                  disabled={googleSyncing}
                  style={{ padding: "6px 12px", background: COLORS.accentGlow, border: `1px solid ${COLORS.accent}44`, borderRadius: 8, color: COLORS.accent, cursor: googleSyncing ? "not-allowed" : "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 13, flexShrink: 0 }}
                >
                  {googleSyncing ? "Syncing..." : "⟳ Sync Now"}
                </button>
              )}
            </div>

            {!googleSyncStatus?.connected ? (
              <div style={{ background: COLORS.bg, borderRadius: 10, padding: "12px 14px", border: `1px solid ${COLORS.border}` }}>
                <p style={{ color: COLORS.amber, fontSize: 13, fontWeight: 600, margin: "0 0 4px" }}>⚠ Google account not linked</p>
                <p style={{ color: COLORS.textMuted, fontSize: 12, margin: 0 }}>Sign in with Google to enable Drive sync.</p>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", background: COLORS.greenGlow, border: `1px solid ${COLORS.green}33`, borderRadius: 10 }}>
                <span style={{ color: COLORS.green, fontSize: 16 }}>✓</span>
                <div>
                  <div style={{ color: COLORS.green, fontSize: 13, fontWeight: 600 }}>Google Drive connected</div>
                  <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2 }}>
                    {googleSyncStatus?.last_synced_at
                      ? `Last synced ${new Date(googleSyncStatus.last_synced_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
                      : 'Not yet synced — click Sync Now'}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "goals" && (
          <SavingsDebtTab COLORS={COLORS} accounts={accounts} />
        )}
        {activeTab === "team" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
              <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 14, margin: "0 0 14px" }}>Household Members</h3>
              {household?.members?.map((m, i) => (
                <div key={m.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: i < household.members.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: COLORS.accentGlow, border: `1px solid ${COLORS.accent}44`, display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.accent, fontWeight: 800, fontSize: 14 }}>
                      {m.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ color: COLORS.text, fontSize: 14, fontWeight: 600 }}>{m.name}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: 11 }}>{m.email}</div>
                    </div>
                  </div>
                  <Badge label={m.role} color={m.role === "owner" ? COLORS.amber : COLORS.accent} />
                </div>
              ))}
              <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                <input value={inviteEmail} onChange={e => { setInviteEmail(e.target.value); setInviteMsg(""); }} placeholder="Email address to invite" style={{ ...inputStyle, flex: 1 }} />
                <button onClick={handleInvite} style={{ padding: "10px 14px", background: COLORS.accent, border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap" }}>Invite</button>
              </div>
              {inviteMsg && <p style={{ color: inviteMsg.includes("added") ? COLORS.green : COLORS.red, fontSize: 13, margin: "8px 0 0" }}>{inviteMsg}</p>}
            </div>

            <button onClick={logout} style={{ width: "100%", padding: "12px", background: "transparent", border: `1px solid ${COLORS.red}44`, borderRadius: 12, color: COLORS.red, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 14 }}>
              Sign Out
            </button>
          </div>
        )}

        {/* ── Appearance tab ── */}
        {activeTab === "appearance" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
              <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 14, margin: "0 0 16px" }}>Choose Theme</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {Object.entries(THEMES).map(([key, theme]) => (
                  <button key={key} onClick={() => setTheme(key)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 12, border: `1px solid ${themeKey === key ? COLORS.accent : COLORS.border}`, background: themeKey === key ? COLORS.accentGlow : COLORS.bg, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                    <span style={{ fontSize: 22 }}>{theme.emoji}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: themeKey === key ? COLORS.accent : COLORS.text, fontWeight: 700, fontSize: 13 }}>{theme.name}</div>
                    </div>
                    {/* Color preview dots */}
                    <div style={{ display: "flex", gap: 4 }}>
                      {[theme.bg, theme.surface, theme.accent, theme.green].map((c, i) => (
                        <div key={i} style={{ width: 12, height: 12, borderRadius: "50%", background: c, border: "1px solid rgba(255,255,255,0.1)" }} />
                      ))}
                    </div>
                    {themeKey === key && <span style={{ color: COLORS.accent, fontSize: 14, fontWeight: 700 }}>✓</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Settings;
